/**
 * MeetingDebriefView - Renders structured meeting debrief JSON
 *
 * Exports three separate components for each meeting type:
 * - InternalMeetingDebriefView
 * - OtherMeetingDebriefView (customer operational meetings)
 * - SalesMeetingDebriefView
 *
 * Handles internal, other, and sales meeting formats from the AI summary.
 */

import React from 'react';
import { Text, Flex } from '@upsales/components';
import BemClass from '@upsales/components/Utils/bemClass';
import { useTranslation } from '../../Helpers/translate';
import type {
	InternalMeetingDebrief,
	OtherMeetingDebrief,
	SalesMeetingDebrief,
	Topic,
	Person,
	ContextInsight,
	WatchOut,
	BlockerRisk,
	GapOpportunity
} from 'App/resources/Model/MeetingDebrief';

type MeetingDebrief = InternalMeetingDebrief | OtherMeetingDebrief | SalesMeetingDebrief;
import './MeetingDebriefView.scss';

// ============================================================================
// Helper Components (shared between all meeting types)
// ============================================================================

interface SectionProps {
	title: string;
	children: React.ReactNode;
}

function Section({ title, children }: SectionProps) {
	return (
		<Flex direction="column" gap="u3">
			<Text size="xs" bold color="grey-8">
				{title.toUpperCase()}
			</Text>
			{children}
		</Flex>
	);
}

interface BulletPointProps {
	children: React.ReactNode;
}

function BulletPoint({ children }: BulletPointProps) {
	const classes = new BemClass('MeetingDebriefView');
	return (
		<Flex gap="10" alignItems="flex-start" spacing="pl2">
			<span className={classes.elem('bulletDot').b()} />
			<Text size="sm" color="grey-11" style={{ flex: 1, lineHeight: 1.5 }}>
				{children}
			</Text>
		</Flex>
	);
}

// ============================================================================
// Status Badge Component (consolidated from SolidBadge, HealthBadge, SeverityBadge)
// ============================================================================

type StatusBadgeVariant = 'solid' | 'health' | 'severity';

interface StatusBadgeProps {
	variant: StatusBadgeVariant;
	value: string | boolean;
}

function StatusBadge({ variant, value }: StatusBadgeProps) {
	const classes = new BemClass('MeetingDebriefView');

	const normalizedValue = typeof value === 'boolean' ? String(value) : value.toLowerCase().replace('-', '_');

	let displayText: string;
	if (variant === 'solid') {
		displayText = value ? '✓ SOLID' : '✗ WEAK';
	} else {
		displayText = String(value).toUpperCase().replace('-', ' ').replace('_', ' ');
	}

	let modifierClass = '';
	if (variant === 'solid') {
		modifierClass = value ? 'solid' : 'weak';
	} else if (variant === 'health') {
		modifierClass = normalizedValue;
	} else if (variant === 'severity') {
		modifierClass = normalizedValue;
	}

	return (
		<span className={classes.elem('statusBadge').mod(variant).mod(modifierClass).b()}>
			<Text size="xs" bold>
				{displayText}
			</Text>
		</span>
	);
}

// ============================================================================
// Helper Functions
// ============================================================================

const parseDebrief = <T,>(data: any): T | null => {
	if (!data) {
		return null;
	}

	if (typeof data === 'object' && data !== null) {
		return data as T;
	}

	if (typeof data === 'string') {
		try {
			return JSON.parse(data) as T;
		} catch (error) {
			console.error('Failed to parse debrief JSON:', error);
			return null;
		}
	}

	return null;
};

const formatStatus = (status: string): string => {
	return status.replace(/_/g, ' ').replace(/-/g, ' ');
};

// ============================================================================
// Internal Meeting Debrief View
// ============================================================================

interface InternalMeetingDebriefViewProps {
	debrief: InternalMeetingDebrief | string | null;
	t: (text: string) => string;
}

export function InternalMeetingDebriefView({ debrief: rawDebrief, t }: InternalMeetingDebriefViewProps) {
	const classes = new BemClass('MeetingDebriefView');

	const debrief = parseDebrief<InternalMeetingDebrief>(rawDebrief);

	if (!debrief) {
		return (
			<Flex direction="column" gap="u6" spacing="pl4 pr4 pb4 pt4" style={{ backgroundColor: '#fff' }}>
				<Text>{t('meetingDebrief.noSummaryAvailable')}</Text>
			</Flex>
		);
	}

	return (
		<Flex direction="column" gap="u6" spacing="pl4 pr4 pb4 pt4" style={{ backgroundColor: '#fff' }}>
			<Section title={t('meetingDebrief.summary')}>
				<Text size="md" color="grey-13" style={{ lineHeight: 1.5, letterSpacing: '0.3px' }}>
					{debrief.summary}
				</Text>
			</Section>

			{debrief.discussed && debrief.discussed.length > 0 ? (
				<Section title={t('meetingDebrief.whatWeDiscussed')}>
					{debrief.discussed.map((topic, idx) => (
						<Flex key={`discussed-${idx}`} direction="column" gap="6" spacing="mb6">
							<Text size="sm" bold color="black">
								{topic.topic}
							</Text>
							{topic.points.map((point, pIdx) => (
								<BulletPoint key={`point-${idx}-${pIdx}`}>{point}</BulletPoint>
							))}
						</Flex>
					))}
				</Section>
			) : null}

			{debrief.blockers && debrief.blockers.length > 0 ? (
				<Section title={t('meetingDebrief.blockers')}>
					{debrief.blockers.map((blocker, idx) => {
						const status = blocker.status.toLowerCase();
						const isResolved = status === 'resolved';
						const isInProgress = status === 'in progress' || status === 'in_progress';
						const statusMod = isResolved ? 'resolved' : isInProgress ? 'in_progress' : 'unresolved';

						return (
							<Flex
								key={`blocker-${idx}`}
								direction="column"
								gap="6"
								className={classes.elem('internalBlockerCard').mod(statusMod).b()}
							>
								<Text size="sm" bold color="black">
									{blocker.issue}
								</Text>
								<Flex gap="6" alignItems="center">
									<span className={classes.elem('statusIndicator').mod(statusMod).b()}>
										{isResolved ? '✓' : '✗'}
									</span>
									<Text size="sm" color="grey-10">
										{blocker.status}
									</Text>
								</Flex>
							</Flex>
						);
					})}
				</Section>
			) : null}

			{debrief.actions && debrief.actions.length > 0 ? (
				<>
					<hr className={classes.elem('divider').b()} />
					<Section title={t('meetingDebrief.actionItems')}>
						{debrief.actions.map((action, idx) => (
							<Flex key={`action-${idx}`} direction="column" gap="u1" spacing="mb6">
								<Text size="xs" bold color="black">
									{action.owner.toUpperCase()}
									{action.deadline && ` · ${action.deadline}`}
								</Text>
								<Text size="sm" color="grey-11">
									{action.task}
								</Text>
							</Flex>
						))}
					</Section>
				</>
			) : null}
		</Flex>
	);
}

// ============================================================================
// Other Meeting Debrief View (Customer operational meetings)
// ============================================================================

interface OtherMeetingDebriefViewProps {
	debrief: OtherMeetingDebrief | string | null;
	t: (text: string) => string;
}

export function OtherMeetingDebriefView({ debrief: rawDebrief, t }: OtherMeetingDebriefViewProps) {
	const classes = new BemClass('MeetingDebriefView');

	const debrief = parseDebrief<OtherMeetingDebrief>(rawDebrief);

	if (!debrief) {
		return (
			<Flex direction="column" gap="u6" spacing="pl4 pr4 pb4 pt4" style={{ backgroundColor: '#fff' }}>
				<Text>{t('meetingDebrief.noSummaryAvailable')}</Text>
			</Flex>
		);
	}

	return (
		<Flex direction="column" gap="u6" spacing="pl4 pr4 pb4 pt4" style={{ backgroundColor: '#fff' }}>
			<Section title={t('meetingDebrief.summary')}>
				<Text size="md" color="grey-13" style={{ lineHeight: 1.5, letterSpacing: '0.3px' }}>
					{debrief.summary}
				</Text>
			</Section>

			{debrief.customer_health ? (
				<Section title={t('meetingDebrief.customerHealth')}>
					<Flex
						direction="column"
						gap="u3"
						alignItems="flex-start"
						className={classes.elem('healthCard').mod(debrief.customer_health.status.replace('-', '_')).b()}
					>
						<StatusBadge variant="health" value={debrief.customer_health.status} />
						{debrief.customer_health.signals?.map((signal, idx) => (
							<BulletPoint key={`signal-${idx}`}>{signal}</BulletPoint>
						))}
					</Flex>
				</Section>
			) : null}

			{debrief.discussed && debrief.discussed.length > 0 ? (
				<Section title={t('meetingDebrief.whatWeDiscussed')}>
					{debrief.discussed.map((topic, idx) => (
						<Flex key={`discussed-${idx}`} direction="column" gap="6" spacing="mb6">
							<Text size="sm" bold color="black">
								{topic.topic}
							</Text>
							{topic.points.map((point, pIdx) => (
								<BulletPoint key={`point-${idx}-${pIdx}`}>{point}</BulletPoint>
							))}
						</Flex>
					))}
				</Section>
			) : null}

			{debrief.blockers_and_risks && debrief.blockers_and_risks.length > 0 ? (
				<Section title={t('meetingDebrief.blockersRisks')}>
					{debrief.blockers_and_risks.map((blocker: BlockerRisk, idx: number) => (
						<Flex
							key={`blocker-${idx}`}
							direction="column"
							gap="u2"
							className={classes.elem('blockerCard').mod(blocker.severity).b()}
						>
							<Text size="sm" bold color="black">
								{blocker.issue}
							</Text>
							<Flex gap="u1" alignItems="center" style={{ flexWrap: 'wrap' }}>
								<StatusBadge variant="severity" value={blocker.severity} />
								<Text size="sm" color="grey-10">
									{' · '}
									{t('meetingDebrief.owner')}
									{': '}
									{blocker.owner}
									{' · '}
									{formatStatus(blocker.status)}
								</Text>
							</Flex>
						</Flex>
					))}
				</Section>
			) : null}

			{debrief.gaps_and_opportunities && debrief.gaps_and_opportunities.length > 0 ? (
				<Section title={t('meetingDebrief.gapsOpportunities')}>
					{debrief.gaps_and_opportunities.map((gap: GapOpportunity, idx: number) => (
						<Flex key={`gap-${idx}`} direction="column" gap="u2" className={classes.elem('gapCard').b()}>
							<Text size="sm" color="grey-11">
								{gap.observation}
							</Text>
							<Text size="sm" className={classes.elem('gapAction').b()}>
								{'→ '}
								{gap.opportunity}
							</Text>
						</Flex>
					))}
				</Section>
			) : null}

			{debrief.actions && debrief.actions.length > 0 && (
				<>
					<hr className={classes.elem('divider').b()} />
					<Section title={t('meetingDebrief.actionItems')}>
						{debrief.actions.map((action, idx) => (
							<Flex key={`action-${idx}`} direction="column" gap="u1" spacing="mb6">
								<Text size="xs" bold color="black">
									{action.owner.toUpperCase()}
									{action.deadline && ` · ${action.deadline}`}
								</Text>
								<Text size="sm" color="grey-11">
									{action.task}
								</Text>
							</Flex>
						))}
					</Section>
				</>
			)}
		</Flex>
	);
}

// ============================================================================
// Sales Meeting Debrief View
// ============================================================================

interface SalesMeetingDebriefViewProps {
	debrief: SalesMeetingDebrief | string | null;
	t: (text: string) => string;
}

export function SalesMeetingDebriefView({ debrief: rawDebrief, t }: SalesMeetingDebriefViewProps) {
	const classes = new BemClass('MeetingDebriefView');

	const debrief = parseDebrief<SalesMeetingDebrief>(rawDebrief);

	if (!debrief) {
		return (
			<Flex direction="column" gap="u6" spacing="pl4 pr4 pb4 pt4" style={{ backgroundColor: '#fff' }}>
				<Text>{t('meetingDebrief.noSummaryAvailable')}</Text>
			</Flex>
		);
	}

	return (
		<Flex direction="column" gap="u6" spacing="pl4 pr4 pb4 pt4" style={{ backgroundColor: '#fff' }}>
			<Section title={t('meetingDebrief.summary')}>
				<Text size="md" color="grey-13" style={{ lineHeight: 1.5, letterSpacing: '0.3px' }}>
					{debrief.summary}
				</Text>
			</Section>

			{debrief.what_happened?.people && debrief.what_happened.people.length > 0 ? (
				<Section title={t('meetingDebrief.people')}>
					{debrief.what_happened.people.map((person: Person, idx: number) => (
						<Text key={`person-${idx}`} size="sm" color="grey-11" spacing="mb2">
							<Text size="sm" bold>
								{person.name}
							</Text>
							{' · '}
							{person.role}
							{' · '}
							{person.stance}
							{person.note && ` — ${person.note}`}
						</Text>
					))}
				</Section>
			) : null}

			{debrief.deal_reality ? (
				<Section title={t('meetingDebrief.dealReality')}>
					<Flex direction="column" gap="u3" className={classes.elem('dealRealityCard').b()}>
						<Text size="sm" bold color="black">
							{debrief.deal_reality.overall}
						</Text>
						<Flex direction="column" gap="6">
							<Text size="sm" color="grey-11">
								<Text size="sm" bold>
									{t('meetingDebrief.budget')}
								</Text>{' '}
								{debrief.deal_reality.budget}
							</Text>
							<Text size="sm" color="grey-11">
								<Text size="sm" bold>
									{t('meetingDebrief.timeline')}
								</Text>{' '}
								{debrief.deal_reality.timeline}
							</Text>
							<Text size="sm" color="grey-11">
								<Text size="sm" bold>
									{t('meetingDebrief.decisionMaker')}
								</Text>{' '}
								{debrief.deal_reality.decision_maker}
							</Text>
							<Text size="sm" color="grey-11">
								<Text size="sm" bold>
									{t('meetingDebrief.competition')}
								</Text>{' '}
								{debrief.deal_reality.competition}
							</Text>
							{debrief.what_happened?.momentum ? (
								<Text size="sm" color="grey-11">
									<Text size="sm" bold>
										{t('meetingDebrief.momentum')}
									</Text>{' '}
									{debrief.what_happened.momentum}
								</Text>
							) : null}
						</Flex>
					</Flex>
				</Section>
			) : null}

			{debrief.three_whys ? (
				<Section title={t('meetingDebrief.threeWhys')}>
					<Flex direction="column" gap="u1" spacing="mb6">
						<Flex gap="u2" alignItems="center">
							<Text size="sm" bold color="black">
								{t('meetingDebrief.whyChange')}
							</Text>
							<StatusBadge variant="solid" value={debrief.three_whys.why_change?.solid || false} />
						</Flex>
						<Text size="sm" color="grey-11">
							{debrief.three_whys.why_change?.status}
						</Text>
					</Flex>

					<Flex direction="column" gap="u1" spacing="mb6">
						<Flex gap="u2" alignItems="center">
							<Text size="sm" bold color="black">
								{t('meetingDebrief.whyNow')}
							</Text>
							<StatusBadge variant="solid" value={debrief.three_whys.why_now?.solid || false} />
						</Flex>
						<Text size="sm" color="grey-11">
							{debrief.three_whys.why_now?.status}
						</Text>
					</Flex>

					<Flex direction="column" gap="u1" spacing="mb6">
						<Flex gap="u2" alignItems="center">
							<Text size="sm" bold color="black">
								{t('meetingDebrief.whyUs')}
							</Text>
							<StatusBadge variant="solid" value={debrief.three_whys.why_us?.solid || false} />
						</Flex>
						<Text size="sm" color="grey-11">
							{debrief.three_whys.why_us?.status}
						</Text>
					</Flex>
				</Section>
			) : null}

			{debrief.what_happened ? (
				<Section title={t('meetingDebrief.whatHappened')}>
					{debrief.what_happened.topics?.map((topic: Topic, idx: number) => (
						<Flex key={`topic-${idx}`} direction="column" gap="6" spacing="mb6">
							<Text size="sm" bold color="black">
								{topic.topic}
							</Text>
							{topic.points.map((point: string, pIdx: number) => (
								<BulletPoint key={`point-${idx}-${pIdx}`}>{point}</BulletPoint>
							))}
						</Flex>
					))}

					{debrief.what_happened.new_info && debrief.what_happened.new_info.length > 0 ? (
						<Flex direction="column" gap="6" spacing="mt6 pt6" className={classes.elem('subSection').b()}>
							<Text size="xs" bold color="grey-8">
								{t('meetingDebrief.newInformation')}
							</Text>
							{debrief.what_happened.new_info.map((info: string, idx: number) => (
								<Text key={`info-${idx}`} size="sm" color="grey-11">
									{'+ '}
									{info}
								</Text>
							))}
						</Flex>
					) : null}
				</Section>
			) : null}

			{debrief.context_insights && debrief.context_insights.length > 0 ? (
				<Section title={t('meetingDebrief.fromYourResearch')}>
					{debrief.context_insights.map((insight: ContextInsight, idx: number) => (
						<Text key={`insight-${idx}`} size="sm" color="grey-11" spacing="mb2">
							<Text size="sm" bold>
								{insight.insight}
							</Text>
							{' → '}
							{insight.implication}
						</Text>
					))}
				</Section>
			) : null}

			{debrief.watch_out && debrief.watch_out.length > 0 ? (
				<Section title={t('meetingDebrief.watchOut')}>
					{debrief.watch_out.map((item: WatchOut, idx: number) => (
						<Flex key={`watch-${idx}`} direction="column" gap="u1" spacing="mb4">
							<Text size="sm" bold color="red">
								{item.issue}
							</Text>
							<Text size="sm" color="grey-11">
								{'→ '}
								{item.action}
							</Text>
						</Flex>
					))}
				</Section>
			) : null}

			{debrief.next_steps ? (
				<>
					<hr className={classes.elem('divider').b()} />
					<Section title={t('meetingDebrief.nextSteps')}>
						{debrief.next_steps.actions?.map((action, idx) => (
							<Flex key={`next-${idx}`} direction="column" gap="u1" spacing="mb6">
								<Text size="xs" bold color="black">
									{action.owner.toUpperCase()}
									{action.deadline && ` · ${action.deadline}`}
								</Text>
								<Text size="sm" color="grey-11">
									{action.task}
								</Text>
							</Flex>
						))}

						{debrief.next_steps.questions_for_next_time &&
						debrief.next_steps.questions_for_next_time.length > 0 ? (
							<Flex
								direction="column"
								gap="6"
								spacing="mt6 pt6"
								className={classes.elem('subSection').b()}
							>
								<Text size="xs" bold color="grey-8">
									{t('meetingDebrief.questionsForNextTime')}
								</Text>
								{debrief.next_steps.questions_for_next_time.map((question: string, idx: number) => (
									<BulletPoint key={`question-${idx}`}>{question}</BulletPoint>
								))}
							</Flex>
						) : null}
					</Section>
				</>
			) : null}
		</Flex>
	);
}

// ============================================================================
// Default Export - Auto-detects meeting type and renders appropriate view
// ============================================================================

interface MeetingDebriefViewProps {
	debrief: MeetingDebrief | string | null;
	forceEnglish?: boolean;
}

export default function MeetingDebriefView({ debrief: rawDebrief, forceEnglish }: MeetingDebriefViewProps) {
	const { t } = useTranslation(undefined, { lng: forceEnglish ? 'en-US' : undefined });
	const debrief = parseDebrief<MeetingDebrief>(rawDebrief);

	if (!debrief) {
		return null;
	}

	if (!('meeting_type' in debrief)) {
		return null;
	}

	switch (debrief.meeting_type) {
		case 'internal':
			return <InternalMeetingDebriefView debrief={debrief} t={t} />;
		case 'other':
			return <OtherMeetingDebriefView debrief={debrief} t={t} />;
		case 'sales':
			return <SalesMeetingDebriefView debrief={debrief} t={t} />;
		default:
			return null;
	}
}
